import Styles from './PostStatusBadge.module.scss'
import cx from 'classnames'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { Tooltip } from 'components/Tooltip/Tooltip'

import { StatusPill } from './StatusPill'
import { AlertTriangle } from 'blaze-ui/icons'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useEmailIntegrations } from 'hooks/useEmailIntegrations'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useStatus } from 'hooks/calendar/useStatus'
import { useValidationErrors } from 'hooks/calendar/useValidationErrors'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { useMemo } from 'react'
import { pluralizeWithNumber } from 'utils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useModals } from 'almanac-ui/Modal'
import { ActivationConnectModal } from 'components/ActivationConnectModal'

dayjs.extend(utc)

export interface PostStatusBadgeProps {
  event: AlmanacFileCalendarEvent
  size?: 'xs' | 'sm' | 'md'
}

export const PostStatusBadge = ({ event, size = 'md' }: PostStatusBadgeProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const workspace = useCurrentWorkspace()
  const { scheduled_post, scheduled_post_targets } = event
  const { openModal } = useModals()
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { connectedEmailIntegration } = useEmailIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()
  const isV2 = brandPlan?.has_content_strategies

  const { validationErrors } = useValidationErrors({ event })
  const {
    isApproved,
    isDraft,
    isFailed,
    isHybrid,
    isNew,
    isPosted,
    isUpdating,
    isWarning,
    isPaused,
    needsApproval,
    targetAnalysis,
  } = useStatus({
    event,
    validationErrors,
    workspace,
    connectedBlogIntegration,
    connectedEmailIntegration,
  })

  const isPastPostAt = event.post_at && dayjs.utc(event.post_at).isBefore(dayjs.utc())
  const pastPostAtTooltip = isPastPostAt
    ? 'The scheduled time passed and no connected accounts were available to publish.'
    : null

  const hasAtLeastOneConnectedTarget = !!scheduled_post_targets?.some((target) => !!target.external_integration_id)
  const connectionsMissingTooltip = useMemo(() => {
    const connectionsMissing = scheduled_post_targets?.filter((target) => !target.external_integration_id).length ?? 0
    if (connectionsMissing === 0) return null
    if (connectionsMissing === scheduled_post_targets?.length)
      return `Connect ${pluralizeWithNumber(connectionsMissing, 'account')} to start auto-posting`
    return `${connectionsMissing} of ${scheduled_post_targets?.length} ${pluralizeWithNumber(
      connectionsMissing,
      'account',
    )} connected`
  }, [scheduled_post_targets])

  let alertIconSizeV2 = size === 'xs' ? 14 : 16

  const handleClick = () => {
    if (hasAtLeastOneConnectedTarget) return

    openModal(ActivationConnectModal, {}, { newStack: true })
  }

  return (
    <div className={Styles.pillsContainer}>
      {isDraft && !isPosted && !isFailed && (
        <StatusPill
          testId="draft-badge"
          status="Draft"
          postStatus="draft"
          color={isSystemRedesignEnabled ? '#EAEDF1' : '#E5ECF3'}
          fontColor={isSystemRedesignEnabled ? 'rgba(52, 64, 84, 1)' : '#262B35'}
          size={size}
        />
      )}
      {isNew && (
        <StatusPill testId="new-badge" status="New" postStatus="new" color="#FCE6FE" fontColor="#A229FE" size={size} />
      )}
      {isFailed && !isHybrid && (
        <Tooltip
          label={scheduled_post?.error_message || validationErrors.join('\n') || pastPostAtTooltip}
          placement="top"
          hide={!(scheduled_post?.error_message || validationErrors.length > 0 || pastPostAtTooltip)}
        >
          <div>
            <StatusPill
              testId="failed-badge"
              status="Failed"
              postStatus="failed"
              color={isSystemRedesignEnabled ? '#FDECEC' : 'rgba(229, 53, 53, 1)'}
              fontColor={isSystemRedesignEnabled ? 'var(--red-70)' : 'var(--light-100)'}
              size={size}
            />
          </div>
        </Tooltip>
      )}
      {isPosted && (
        <StatusPill
          testId="posted-badge"
          status="Posted"
          postStatus="posted"
          color={isSystemRedesignEnabled ? '#ECFDF3' : 'rgba(214, 255, 230, 1)'}
          fontColor={isSystemRedesignEnabled ? 'rgba(7, 148, 42, 1)' : 'rgba(0, 81, 8, 1)'}
          size={size}
        />
      )}
      {isUpdating && (
        <StatusPill
          testId="updating-badge"
          status="Updating"
          postStatus="updating"
          color="rgba(177, 190, 205, 1)"
          fontColor="var(--dark-80)"
          size={size}
        />
      )}
      {needsApproval && !isPosted && !isFailed && (
        <StatusPill
          testId="review-badge"
          status="Review"
          postStatus="review"
          color="#F8F0FF"
          fontColor="#A229FE"
          size={size}
        />
      )}
      {isApproved && !isFailed && !isPosted && (
        <StatusPill
          testId="approved-badge"
          status="Approved"
          postStatus="approved"
          color="#EFF7FF"
          fontColor="#157EEF"
          size={size}
        />
      )}
      {isPaused && (
        <StatusPill
          testId="paused-badge"
          status="Paused"
          postStatus="paused"
          color="#EAEDF1"
          fontColor="#344054"
          size={size}
        />
      )}
      {(isWarning || (isApproved && !hasAtLeastOneConnectedTarget)) && (
        <Tooltip
          label={scheduled_post?.error_message || validationErrors.join('\n') || connectionsMissingTooltip}
          placement="top"
          hide={!(scheduled_post?.error_message || validationErrors.length > 0 || connectionsMissingTooltip)}
        >
          <div
            className={cx(Styles.statusCornerContainer, { [Styles.clickable]: !hasAtLeastOneConnectedTarget })}
            onClick={handleClick}
          >
            <div className={cx(Styles.warningPill, Styles[size], { [Styles.isV2]: isV2 })} data-testid="warning-badge">
              <AlertTriangle size={isV2 ? alertIconSizeV2 : 20} color="var(--dark-80)" />
            </div>
          </div>
        </Tooltip>
      )}
      {isHybrid && !targetAnalysis.useOldLogic && (
        <Tooltip
          label={`${targetAnalysis.successfulTargets.length} successful, ${targetAnalysis.failedTargets.length} failed`}
          placement="top"
        >
          <div
            className={cx(Styles.hybridAlert, Styles[size], { [Styles.isV2]: isV2 })}
            data-testid="hybrid-warning-badge"
          >
            <AlertTriangle
              size={isV2 ? alertIconSizeV2 : 16}
              color={isSystemRedesignEnabled ? 'var(--light-100)' : 'var(--negative-60)'}
            />
          </div>
        </Tooltip>
      )}
    </div>
  )
}
